import { Button, Input, InputStructure } from '@tablecheck/tablekit-react';
import { AlertAppearance } from '@tablekit/alert';
import axios from 'axios';
import queryString from 'query-string';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { SignInLink } from 'components/SignInLink';
import { usePasswordRequirements } from 'pages/ResetPassword/usePasswordRequirements';
import { getCsrfToken } from 'utils/auth';
import { API_ENDPOINTS } from 'utils/constants';
import { AuthFormWrapper, FormAlert, FormFooter } from 'utils/form.styles';
import { onInputFocus } from 'utils/input';
import { parseError } from 'utils/parseError';

function InvalidTokenAlert() {
  const [t] = useTranslation();

  return (
    <FormAlert
      appearance={AlertAppearance.Danger}
      id="error"
      isShow
      data-test-id="alert"
      shouldUsePortal={false}
      isDismissible={false}
    >
      {t('forms.invalid_token')}
    </FormAlert>
  );
}

export function ForgotPasswordPage(): JSX.Element {
  const location = useLocation();
  const [email, setEmail] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [isLoading, setLoading] = React.useState(false);
  const [isDone, setDone] = React.useState(false);
  const [t, { language }] = useTranslation();
  const { token } = queryString.parse(location.search);
  const { isExpiredOrInvalidToken } = usePasswordRequirements(token);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    setLoading(true);
    axios({
      method: 'post',
      url: API_ENDPOINTS.RESET_PASSWORD,
      data: {
        username: email,
        locale: language,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        _csrf_token: getCsrfToken()
      },
      headers: {
        accept: 'application/json'
      }
    })
      .then(() => {
        setDone(true);
        setLoading(false);
        return true;
      })
      .catch((respError) => {
        setError(parseError(respError.response.data.error));
        setLoading(false);
      });

    event.preventDefault();
  };

  return (
    <AuthFormWrapper onSubmit={onSubmit}>
      <h2>{t('forms.forgot_password')}</h2>
      {error && (
        <FormAlert
          appearance={AlertAppearance.Danger}
          isShow
          shouldUsePortal={false}
          isDismissible={false}
        >
          {t(error)}
        </FormAlert>
      )}
      {isExpiredOrInvalidToken ? <InvalidTokenAlert /> : null}
      {isDone ? (
        <FormAlert
          appearance={AlertAppearance.Primary}
          isShow
          shouldUsePortal={false}
          isDismissible={false}
        >
          {t('forms.password_reset_send_instructions')}
        </FormAlert>
      ) : (
        <>
          <InputStructure
            name="email"
            label={t('forms.email')}
            input={
              <Input
                type="email"
                id="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                onFocus={onInputFocus}
                placeholder={t('forms.email')}
                data-variant={isLoading ? 'disabled' : undefined}
                data-stretch
                required
              />
            }
          />
          <Button
            data-size="large"
            disabled={isLoading}
            aria-busy={isLoading}
            type="submit"
            aria-label="Submit button"
          >
            {t('forms.reset_password_instructions')}
          </Button>
        </>
      )}
      <FormFooter>
        <SignInLink />
      </FormFooter>
    </AuthFormWrapper>
  );
}
